import { computeTotal, widgetBooking, widgetPrices } from '#lib/widget/booking/booking.js';
import { widgetContent } from '#lib/widget/core.js';
import { widgetMyBooking } from '#lib/widget/my-booking/myBooking.js';
import { get } from 'svelte/store';

interface EcommerceItem {
	item_id: string;
	item_name?: string;
	price?: number;
	quantity?: number;
	[key: string]: string | number | boolean | undefined;
}

interface EcommercePayload {
	currency?: string;
	value?: number;
	transaction_id?: string;
	items?: EcommerceItem[];
}

export function pushToDataLayer(eventData: Record<string, unknown>): void {
	if (typeof window === 'undefined') return;
	// @ts-ignore
	window.dataLayer = window.dataLayer || [];
	// @ts-ignore
	window.dataLayer.push(eventData);

	if (window.parent !== window) {
		window.parent.postMessage(
			{
				source: 'reservatoo-widget',
				...eventData
			},
			'*'
		);
	}
}

export function dispatchEcommerceEvent(
	eventName: 'add_to_cart' | 'begin_checkout' | 'purchase'
): void {
	if (typeof window === 'undefined') return;

	let payload: EcommercePayload = {
		currency: 'EUR',
		value: 0,
		items: []
	};

	if (eventName === 'purchase') {
		const myBooking = get(widgetMyBooking);
		if (myBooking) {
			payload.transaction_id = myBooking.id;
			payload.value = myBooking.pricesSummary?.total ?? 0;

			// Offres (prix de base)
			payload.items = myBooking.offers.map((offer) => ({
				item_id: offer.id,
				item_name: offer.name,
				quantity: 1
			}));

			// Guest Prices
			if (myBooking.pricesSummary?.guestPrices) {
				const offerName = myBooking.offers[0]?.name ? `${myBooking.offers[0].name} - ` : '';
				myBooking.pricesSummary.guestPrices.forEach((gp, idx) => {
					payload.items!.push({
						item_id: `gp_${idx}`,
						item_name: `${offerName} - ${gp.name}`,
						price: gp.unitPrice,
						quantity: gp.quantity
					});
				});
			}

			// Produits additionnels
			if (myBooking.pricesSummary?.products) {
				myBooking.pricesSummary.products.forEach((p, idx) => {
					payload.items!.push({
						item_id: `prod_${idx}`,
						item_name: p.name,
						price: p.unitPrice,
						quantity: p.quantity
					});
				});
			}
		}
	} else {
		const booking = get(widgetBooking);
		const prices = get(widgetPrices) || [];

		payload.value = computeTotal(booking);

		if (booking.offer && booking.offer.basePrice > 0) {
			payload.items!.push({
				item_id: booking.offer.id,
				item_name: booking.offer.name,
				price: booking.offer.basePrice,
				quantity: 1
			});
		}

		Object.entries(booking.prices).forEach(([priceId, quantity]) => {
			if (quantity > 0) {
				const price = prices.find((p) => p.id === priceId);
				if (price) {
					payload.items!.push({
						item_id: price.id,
						item_name: booking.offer
							? `${booking.offer.name} - ${price.name}`
							: price.name,
						price: price.price,
						quantity: quantity
					});
				}
			}
		});

		const content = get(widgetContent);
		Object.entries(booking.products).forEach(([productId, quantity]) => {
			if (quantity > 0) {
				const product = content.products.find((p) => p.id === productId);
				if (product) {
					payload.items!.push({
						item_id: product.id,
						item_name: product.title,
						price: product.price,
						quantity: quantity
					});
				}
			}
		});
	}

	pushToDataLayer({
		event: eventName,
		ecommerce: payload
	});
}
